// Chat screen — wired to the typed API via TanStack Query.
//
// The thread (messages list) and session lifecycle live in the Zustand
// store at @/stores/session so navigation Chat → Recommendation → back
// keeps the conversation intact.
//
// On first mount, creates a session via useCreateSessionMutation (the
// store's startSession action seeds the thread with MIMIR's greeting).
// Each user send fires useSendMessageMutation; the optimistic onMutate
// appends the user bubble immediately so the UI never has a void between
// send and reply. When the response carries intake_complete=true, the
// store flips and the screen renders an "intake completata" footer with
// a CTA into the Recommendation screen.
//
// MIMIR's typing indicator now reflects the real mutation pending state
// (only shown while a send is in flight).

import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  useActiveSessionQuery,
  useCreateSessionMutation,
  useDeleteLastPairMutation,
  useDeleteSessionMutation,
  useSendMessageMutation,
} from "@/api/queries";
import {
  Button,
  IconButton,
  MimirMark,
  Surface,
  TopBar,
  Wordmark,
} from "@/design-system";
import { colors } from "@/design-system/tokens";
import { useBranding } from "@/lib/branding";
import { useScreenTitle } from "@/lib/use-screen-title";
import { cn } from "@/lib/utils";
import { useSession } from "@/stores/session";

export interface ChatProps {
  onComplete: () => void;
  onBack: () => void;
}

// ICSP-35 (Scenario 1b): when intakeComplete flips true the route's
// "Vedi la mia raccomandazione" button appears on the same render as
// the final assistant message. Pre-hotfix QA users clicked through
// before their eyes settled on the body and never read MIMIR's last
// reply. The fix is a deliberate UI delay: hide the button for
// POST_INTAKE_BUTTON_DELAY_MS, render a hint banner pointing the user
// at the assistant body, then reveal the button. Exported separately
// so the values can be pinned by a future vitest test (per wave-1
// path-(a) precedent: TS-level signature today, vitest if/when infra).
export const POST_INTAKE_BUTTON_DELAY_MS = 2500;

/**
 * ICSP-35 UI-state computer for the post-intake-completion window.
 * Pure function — easy to test in isolation; the component invokes it
 * once per render and acts on the returned discriminator.
 *
 * - "none"        : intake not complete yet, render the live composer.
 * - "hint-banner" : intake just completed, button suppressed for the
 *                   delay window, banner drawing attention to the body.
 * - "button"      : delay elapsed, manual "Vedi la mia raccomandazione"
 *                   button revealed.
 */
export function computePostIntakeUiState(
  intakeComplete: boolean,
  buttonRevealed: boolean,
): "none" | "hint-banner" | "button" {
  if (!intakeComplete) return "none";
  return buttonRevealed ? "button" : "hint-banner";
}

export function Chat({ onComplete, onBack }: ChatProps) {
  useScreenTitle("Conversazione con MIMIR");
  const { displayName } = useBranding();
  const [input, setInput] = useState("");
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // ICSP-35: independent of intakeComplete because it must FLIP only
  // after the delay timer elapses, not synchronously with the store.
  const [buttonRevealed, setButtonRevealed] = useState(false);
  const sessionId = useSession((s) => s.sessionId);
  const thread = useSession((s) => s.thread);
  const intakeComplete = useSession((s) => s.intakeComplete);
  const hydrateSession = useSession((s) => s.hydrateSession);
  const popLastPair = useSession((s) => s.popLastPair);
  const sessionReset = useSession((s) => s.reset);

  const createSession = useCreateSessionMutation();
  const sendMessage = useSendMessageMutation();
  const deleteSession = useDeleteSessionMutation();
  const deleteLastPair = useDeleteLastPairMutation();

  // Index of the most recent user-authored message — used to render an
  // "Modifica" affordance only on the latest user bubble.
  const lastUserIdx = (() => {
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      if (thread[i]?.role === "user") return i;
    }
    return -1;
  })();
  // ICSP-35: index of the most recent assistant turn — the hint banner
  // renders directly above it during the post-intake delay window so
  // "leggi qui sotto" points at the actual message body.
  const lastAssistantIdx = (() => {
    for (let i = thread.length - 1; i >= 0; i -= 1) {
      if (thread[i]?.role === "assistant") return i;
    }
    return -1;
  })();
  const canEditLast =
    !intakeComplete && !sendMessage.isPending && !deleteLastPair.isPending;
  // Resume only if the in-memory store has nothing — otherwise the user is
  // already mid-conversation in this tab.
  const activeQuery = useActiveSessionQuery(!sessionId);
  const threadRef = useRef<HTMLDivElement>(null);

  // Bootstrap: 1) try resuming the user's most recent session; 2) otherwise
  // create a fresh one. Runs once when sessionId is null.
  useEffect(() => {
    if (sessionId) return;
    if (activeQuery.isLoading) return;
    if (activeQuery.data) {
      hydrateSession(activeQuery.data);
      return;
    }
    if (
      activeQuery.isFetched &&
      activeQuery.data === null &&
      createSession.status === "idle"
    ) {
      createSession.mutate();
    }
  }, [
    sessionId,
    activeQuery.isLoading,
    activeQuery.isFetched,
    activeQuery.data,
    hydrateSession,
    createSession,
  ]);

  // Auto-scroll to the newest message when the thread grows.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, sendMessage.isPending]);

  // ICSP-35: when intakeComplete flips true, suppress the "Vedi la
  // raccomandazione" button for POST_INTAKE_BUTTON_DELAY_MS so the user
  // has time to read MIMIR's final reply. While the timer runs the
  // hint banner renders above the last assistant message. When
  // intakeComplete resets (e.g. session reset / new conversation
  // flow), buttonRevealed also resets so the next completion gets the
  // same guarded reveal.
  useEffect(() => {
    if (!intakeComplete) {
      if (buttonRevealed) setButtonRevealed(false);
      return;
    }
    if (buttonRevealed) return;
    const handle = window.setTimeout(
      () => setButtonRevealed(true),
      POST_INTAKE_BUTTON_DELAY_MS,
    );
    return () => window.clearTimeout(handle);
  }, [intakeComplete, buttonRevealed]);

  // ICSP-35: on the intakeComplete flip, scroll so the hint banner +
  // the last assistant message are visible at the top of the scroll
  // area (not at the bottom where the auto-scroll-to-newest would
  // park them with the button-spot in focus). Uses scrollIntoView on
  // the hint anchor element so the user's eye lands on the message
  // body, not on whatever space the button later reveals into.
  useEffect(() => {
    if (!intakeComplete || buttonRevealed) return;
    const el = threadRef.current;
    if (!el) return;
    // Defer one frame so the hint banner is in the DOM before we
    // compute the scroll target.
    const handle = window.requestAnimationFrame(() => {
      const banner = el.querySelector<HTMLElement>('[data-icsp35-anchor="hint"]');
      if (banner) {
        banner.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return () => window.cancelAnimationFrame(handle);
  }, [intakeComplete, buttonRevealed]);

  function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || !sessionId || sendMessage.isPending) return;
    sendMessage.mutate({ sessionId, body: { message: trimmed } });
    setInput("");
  }

  /** Clicked "Indietro": if intake is incomplete and the user has actually
   *  written something, ask whether to save or discard. Otherwise just navigate. */
  function handleBackClick() {
    const userHasWritten = thread.some((m) => m.role === "user");
    if (!intakeComplete && userHasWritten) {
      setShowExitConfirm(true);
    } else {
      onBack();
    }
  }

  function handleExitSave() {
    setShowExitConfirm(false);
    onBack();
  }

  function handleExitDiscard() {
    if (!sessionId) {
      setShowExitConfirm(false);
      onBack();
      return;
    }
    deleteSession.mutate(sessionId, {
      onSuccess: () => {
        sessionReset();
        setShowExitConfirm(false);
        onBack();
      },
    });
  }

  function handleEditLast() {
    if (!sessionId || !canEditLast) return;
    deleteLastPair.mutate(sessionId, {
      onSuccess: (data) => {
        popLastPair();
        if (data.last_user_message) {
          setInput(data.last_user_message);
        }
      },
    });
  }

  function handleRicomincia() {
    if (sessionId) {
      // Wipe the current session entirely (it's incomplete, no need to keep it
      // for audit). Then ask the backend for a fresh one.
      deleteSession.mutate(sessionId, {
        onSuccess: () => {
          sessionReset();
          createSession.reset();
          createSession.mutate();
        },
      });
    } else {
      sessionReset();
      createSession.reset();
      createSession.mutate();
    }
  }

  return (
    <Surface flex>
      <TopBar
        left={
          <div className="flex items-center gap-2.5">
            <IconButton ariaLabel="Indietro" onClick={handleBackClick}>
              <svg
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M8 2L2 8l6 6" />
              </svg>
            </IconButton>
            <Wordmark size={15} displayName={displayName} />
          </div>
        }
        right={
          <div className="flex items-center gap-1">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.6px] text-ink-faint mr-2">
              {intakeComplete ? "Profilo pronto" : "Conversazione in corso"}
            </span>
            <IconButton ariaLabel="Aiuto">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="9" cy="9" r="7" />
                <path
                  d="M7 7c0-1.1.9-2 2-2s2 .9 2 2-2 1.5-2 3M9 12.5v.5"
                  strokeLinecap="round"
                />
              </svg>
            </IconButton>
          </div>
        }
      />

      {/* ─── Thread ─────────────────────────────────────── */}
      <div
        role="log"
        aria-live="polite"
        ref={threadRef}
        className="ics-noscroll flex-1 min-h-0 overflow-y-auto px-5 pt-3.5 pb-5 mx-auto w-full max-w-2xl"
      >
        {createSession.isError && (
          <ErrorRow
            text="Non riesco ad avviare la conversazione."
            onRetry={() => createSession.reset()}
          />
        )}

        {!sessionId && createSession.isPending && (
          <p className="text-center mt-10 text-[13px] text-ink-faint font-mono uppercase tracking-[0.6px]">
            avvio…
          </p>
        )}

        {thread.map((m, i) => {
          const postIntakeUi = computePostIntakeUiState(
            intakeComplete,
            buttonRevealed,
          );
          // ICSP-35: hint banner rides directly above the last assistant
          // message during the delay window. Inline placement (not after
          // the thread) means "leggi qui sotto" points at the actual
          // message body, so the user's eye lands there before the
          // button reveals.
          const showHintBanner =
            postIntakeUi === "hint-banner" &&
            m.role === "assistant" &&
            i === lastAssistantIdx;
          return (
            <Fragment key={m.id}>
              {showHintBanner && <IntakeCompleteHint />}
              {m.role === "assistant" ? (
                isSystemInjected(m.text) ? (
                  <SystemInjectedBubble>
                    {stripSystemInjectedMarkers(m.text)}
                  </SystemInjectedBubble>
                ) : (
                  <MimirBubble>{m.text}</MimirBubble>
                )
              ) : (
                <UserBubble
                  showEdit={i === lastUserIdx && canEditLast}
                  onEdit={handleEditLast}
                >
                  {m.text}
                </UserBubble>
              )}
            </Fragment>
          );
        })}

        {sendMessage.isPending && <Typing />}

        {sendMessage.isError && (
          <ErrorRow
            text="Risposta non riuscita."
            onRetry={() => sendMessage.reset()}
          />
        )}

        {/* ICSP-35: the "Vedi la mia raccomandazione" button reveals
          only after POST_INTAKE_BUTTON_DELAY_MS — the hint banner
          rides above the last assistant message during the delay
          (see thread.map above) so the user reads the body first. */}
        {computePostIntakeUiState(intakeComplete, buttonRevealed) ===
          "button" && (
          <div className="mt-5 flex justify-center">
            <Button variant="primary" onClick={onComplete}>
              Vedi la mia raccomandazione
            </Button>
          </div>
        )}
      </div>

      {/* ─── Composer ───────────────────────────────────── */}
      <div className="px-4 pt-2.5 pb-3 bg-paper border-t border-[rgba(60,50,40,0.08)] shrink-0">
        <div className="flex items-end gap-2 bg-paper-card rounded-[24px] py-2 pr-2 pl-4 shadow-card">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Scrivi a MIMIR…"
            rows={1}
            disabled={!sessionId || intakeComplete}
            className={cn(
              "flex-1 border-none outline-none resize-none bg-transparent",
              "font-sans text-base text-ink py-2",
              "min-h-[24px] max-h-[96px] leading-[1.5]",
              "placeholder:text-ink-faint",
              "disabled:opacity-60",
            )}
          />
          <IconButton ariaLabel="Voce">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="text-ink-muted"
            >
              <rect x="6" y="2" width="6" height="10" rx="3" />
              <path d="M3 9a6 6 0 0012 0M9 15v2" />
            </svg>
          </IconButton>
          <button
            type="button"
            onClick={handleSend}
            aria-label="Invia"
            disabled={!input.trim() || !sessionId || sendMessage.isPending}
            className={cn(
              "flex w-10 h-10 items-center justify-center shrink-0 rounded-full border-none cursor-pointer",
              "bg-rame text-[#FDFBF7] hover:bg-rame-hover",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rame focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
            )}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2L7.5 14 6 9 1 7.5z" />
            </svg>
          </button>
        </div>
        <p className="text-center mt-2.5 text-[11.5px] text-ink-faint">
          MIMIR non condivide nulla con terzi.{" "}
          <button
            type="button"
            disabled={deleteSession.isPending}
            title="Il tuo profilo viene mantenuto — riparte solo la chat."
            className="border-b border-dotted bg-transparent border-none p-0 cursor-pointer text-ink-faint hover:text-ink-muted disabled:opacity-50"
            onClick={handleRicomincia}
          >
            Nuova conversazione
          </button>
        </p>
      </div>

      {showExitConfirm && (
        <ExitConfirmModal
          isDeleting={deleteSession.isPending}
          onSave={handleExitSave}
          onDiscard={handleExitDiscard}
          onCancel={() => setShowExitConfirm(false)}
        />
      )}
    </Surface>
  );
}

// ─── Sub-components ──────────────────────────────────────────

// ICSP-30: the backend wraps assistant turns that the route layer
// injected on the LLM's behalf (underage refusal, missing-field re-ask)
// in `[[SYSTEM_INJECTED]] ... [[/SYSTEM_INJECTED]]`. The LLM sees these
// markers on history rehydration and knows the turn wasn't its own —
// the SISTEMA DI SICUREZZA instruction block in the intake prompt tells
// it how to acknowledge them. The frontend strips the markers before
// rendering AND renders the message with a distinct visual so the user
// understands at a glance that it came from the safety layer, not from
// MIMIR's conversational voice.
const SYSTEM_INJECTED_OPEN = "[[SYSTEM_INJECTED]]";
const SYSTEM_INJECTED_CLOSE = "[[/SYSTEM_INJECTED]]";

function isSystemInjected(text: string): boolean {
  return (
    text.startsWith(SYSTEM_INJECTED_OPEN) && text.endsWith(SYSTEM_INJECTED_CLOSE)
  );
}

function stripSystemInjectedMarkers(text: string): string {
  if (!isSystemInjected(text)) return text;
  return text
    .slice(SYSTEM_INJECTED_OPEN.length, text.length - SYSTEM_INJECTED_CLOSE.length)
    .trim();
}

/**
 * ICSP-35: hint banner rendered above the last assistant message during
 * the post-intake delay window. Tone-matched to the rame brand colour so
 * the user reads it as "MIMIR has a completion note", not as an error.
 * The "leggi qui sotto" copy points at the message body that renders
 * directly underneath this component.
 */
function IntakeCompleteHint() {
  return (
    <div
      className={cn(
        "mb-3 mx-auto max-w-[440px] rounded-xl px-4 py-3",
        "border border-[rgba(184,98,58,0.25)]",
        "text-center",
      )}
      style={{ background: "rgba(184,98,58,0.06)" }}
      role="status"
      aria-live="polite"
      data-icsp35-anchor="hint"
    >
      <div className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-rame mb-1">
        Profilo pronto
      </div>
      <div className="font-serif text-[14.5px] leading-[1.5] text-ink">
        MIMIR ha completato il profilo — leggi qui sotto.
      </div>
    </div>
  );
}

function SystemInjectedBubble({ children }: { children: string }) {
  return (
    <div className="animate-fade-up flex gap-2.5 mb-3 items-start">
      <div className="w-[26px] shrink-0" aria-hidden="true" />
      <div
        className={cn(
          "rounded-2xl px-4 py-3 max-w-[440px]",
          "text-[14.5px] leading-[1.55] text-ink",
          "border border-[rgba(196,150,72,0.25)]",
        )}
        style={{
          background: "rgba(196,150,72,0.08)",
          borderTopLeftRadius: 4,
        }}
        role="status"
      >
        <div className="font-mono text-[10.5px] uppercase tracking-[0.8px] text-amber-warning mb-1.5">
          Sicurezza · automatico
        </div>
        <div className="font-serif">{children}</div>
      </div>
    </div>
  );
}

function MimirBubble({ children }: { children: string }) {
  // Defensive net: strip any internal extraction block / marker the runtime
  // parser may have left behind (partial/streamed responses can slip, and
  // the aggiorna/returning-user flow regressed by leaking a full delta block
  // body — see backend runtime_base fix). The old regex only matched
  // [[EXTRACTION]]…[[/EXTRACTION]] and crucially MISSED [[EXTRACTION_DELTA]]…
  // [[/EXTRACTION_DELTA]] (the `_DELTA` breaks the literal `EXTRACTION]]`).
  // Cover BOTH paired forms first (greedy body removal), then mop up any
  // bare/orphan markers so no internal token ever renders.
  const cleaned = children
    .replace(
      /\[\[EXTRACTION_DELTA\]\][\s\S]*?\[\[\/EXTRACTION_DELTA\]\]/g,
      "",
    )
    .replace(/\[\[EXTRACTION\]\][\s\S]*?\[\[\/EXTRACTION\]\]/g, "")
    .replace(/\[\[\/?EXTRACTION_DELTA\]\]/g, "")
    .replace(/\[\[\/?EXTRACTION\]\]/g, "")
    .replace(/\[\[INTAKE_COMPLETE\]\]/g, "")
    .trim();

  return (
    <div className="animate-fade-up flex gap-2.5 mb-3 items-start">
      <MimirMark size={26} />
      <div
        className={cn(
          "font-serif bg-paper-card rounded-2xl",
          "px-4 py-3 max-w-[440px]",
          "text-[15.5px] leading-[1.55] text-ink shadow-card font-normal",
          // Markdown prose styling — tight, on-brand, no @tailwindcss/typography.
          "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
          "[&_strong]:font-semibold [&_strong]:text-ink",
          "[&_em]:italic",
          "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:marker:text-ink-faint",
          "[&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:marker:text-ink-faint",
          "[&_li]:my-1",
          "[&_h1]:font-serif [&_h1]:text-[18px] [&_h1]:font-medium [&_h1]:my-2",
          "[&_h2]:font-serif [&_h2]:text-[17px] [&_h2]:font-medium [&_h2]:my-2",
          "[&_h3]:font-mono [&_h3]:text-[11px] [&_h3]:uppercase [&_h3]:tracking-[0.6px] [&_h3]:text-ink-faint [&_h3]:mt-3 [&_h3]:mb-1",
          "[&_code]:font-mono [&_code]:text-[13px] [&_code]:bg-paper-muted [&_code]:px-1 [&_code]:py-px [&_code]:rounded",
        )}
        style={{ borderTopLeftRadius: 4 }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
      </div>
    </div>
  );
}

function UserBubble({
  children,
  showEdit = false,
  onEdit,
}: {
  children: string;
  showEdit?: boolean;
  onEdit?: () => void;
}) {
  return (
    <div className="animate-fade-up flex flex-col items-end mb-3">
      <div
        className={cn(
          "rounded-2xl px-4 py-3 max-w-[440px]",
          "text-[15.5px] leading-[1.55] text-ink whitespace-pre-wrap",
        )}
        style={{
          background: "rgba(184,98,58,0.12)",
          borderTopRightRadius: 4,
          border: `0.5px solid rgba(184,98,58,0.18)`,
        }}
      >
        {children}
      </div>
      {showEdit && onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="mt-1 font-mono text-[10.5px] uppercase tracking-[0.6px] text-ink-faint hover:text-ink-muted bg-transparent border-none cursor-pointer"
        >
          ← Modifica
        </button>
      )}
    </div>
  );
}

function Typing() {
  return (
    <div className="flex gap-2.5 items-start mb-2">
      <MimirMark size={26} />
      <div
        className="bg-paper-card rounded-2xl px-4 py-3.5 shadow-card flex gap-1 items-center"
        style={{ borderTopLeftRadius: 4 }}
      >
        {[0, 1, 2].map((i) => {
          const style: CSSProperties = {
            background: colors.inkMuted,
            animationDelay: `${i * 0.15}s`,
          };
          return (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full animate-pulse-dot"
              style={style}
            />
          );
        })}
      </div>
    </div>
  );
}

function ExitConfirmModal({
  isDeleting,
  onSave,
  onDiscard,
  onCancel,
}: {
  isDeleting: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-5"
      onClick={onCancel}
    >
      <div
        className="bg-paper-card rounded-card shadow-elevated max-w-[420px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="exit-confirm-title"
          className="font-serif text-[22px] font-medium text-ink m-0 mb-2"
        >
          Vuoi salvare la conversazione?
        </h2>
        <p className="text-[14px] leading-[1.55] text-ink-muted mt-0 mb-5">
          Non hai ancora completato il profilo. Se salvi puoi riprendere
          dall'ultimo punto la prossima volta. Se la cancelli, sparisce.
        </p>
        <div className="flex flex-col gap-2">
          <Button variant="primary" full onClick={onSave} disabled={isDeleting}>
            Salva e chiudi
          </Button>
          <Button variant="ghost" full onClick={onDiscard} disabled={isDeleting}>
            {isDeleting ? "Cancellazione…" : "Cancella la conversazione"}
          </Button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="mt-1 text-[13px] text-ink-muted bg-transparent border-none cursor-pointer hover:text-ink disabled:opacity-50"
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}

function ErrorRow({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="my-3 mx-auto max-w-[300px] text-center">
      <p className="text-[13px] text-brick-danger mb-2">{text}</p>
      <button
        type="button"
        onClick={onRetry}
        className="font-mono text-[11px] uppercase tracking-[0.6px] text-ink-muted border-b border-dotted border-ink-faint bg-transparent border-x-0 border-t-0 cursor-pointer"
      >
        Riprova
      </button>
    </div>
  );
}
